/**
 * TaskFlowWorkspace.tsx
 *
 * Master Workspace Container for TaskFlow Plugin.
 * 100% Theme-tokenized with fluid typography, responsive view switcher (Board, Calendar,
 * Timeline, WBS, Triage), Multi-Axis Swimlanes, Bounded Query search, BulkActionBar,
 * Automation Rule Builder, Keyboard Shortcuts Modal, Template Picker, and JSON/CSV Export.
 * Fully localized via useTranslation() and RTL-ready.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { TaskFlowItem, TaskFlowItemType } from '../../plugins/taskflow/contracts/taskflow-schema.js';
import { globalTaskFlowStore } from '../../plugins/taskflow/engine/taskflow-store.js';
import { TaskFlowBoardView } from './views/TaskFlowBoardView.js';
import { TaskFlowTimelineView } from './views/TaskFlowTimelineView.js';
import { TaskFlowWbsView } from './views/TaskFlowWbsView.js';
import { TaskFlowTriageView } from './views/TaskFlowTriageView.js';
import { TaskDetailDrawer } from './components/TaskDetailDrawer.js';
import { BulkActionBar } from './components/BulkActionBar.js';
import { SwimlaneContainer, type SwimlaneGroupingAxis } from './components/SwimlaneContainer.js';
import { AutomationRuleEditor } from './components/AutomationRuleEditor.js';
import { KeyboardShortcutsModal } from './components/KeyboardShortcutsModal.js';
import { TemplatePickerModal } from './components/TemplatePickerModal.js';
import { TaskFlowDataPortability } from '../../plugins/taskflow/engine/taskflow-export-import.js';
import { TaskFlowShortcutManager } from '../../plugins/taskflow/engine/taskflow-shortcuts.js';
import { UnifiedCalendarView } from '../calendar/UnifiedCalendarView.js';
import { projectTaskFlowItemToCalendar } from '../../plugins/calendar/calendar-projection-service.js';
import type { HerxCalendarItem } from '../../plugins/calendar/calendar-types.js';
import { useTranslation } from '../../i18n/useTranslation.js';

export type TaskFlowViewMode = 'board' | 'calendar' | 'timeline' | 'wbs' | 'triage';

export interface TaskFlowWorkspaceProps {
  workspaceId?: string;
  initialViewMode?: TaskFlowViewMode;
  isEmbedded?: boolean;
}

export const TaskFlowWorkspace: React.FC<TaskFlowWorkspaceProps> = ({
  workspaceId = 'workspace-primary',
  initialViewMode = 'board',
  isEmbedded = false,
}) => {
  const { t, isRtl } = useTranslation();
  const [viewMode, setViewMode] = useState<TaskFlowViewMode>(initialViewMode);
  const [queryString, setQueryString] = useState('');
  const [selectedTask, setSelectedTask] = useState<TaskFlowItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [swimlaneAxis, setSwimlaneAxis] = useState<SwimlaneGroupingAxis>('none');
  const [isAutomationOpen, setIsAutomationOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importNotification, setImportNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Subscribe to store updates
  useEffect(() => {
    return globalTaskFlowStore.subscribe(() => {
      setRefreshTrigger((prev) => prev + 1);
    });
  }, []);

  // Filtered items based on query
  const items = useMemo(() => {
    if (!queryString.trim()) {
      return globalTaskFlowStore.getItems(workspaceId);
    }
    return globalTaskFlowStore.queryItems(workspaceId, queryString);
  }, [workspaceId, queryString, refreshTrigger]);

  // Projected calendar items for calendar view mode
  const calendarItems = useMemo<HerxCalendarItem[]>(() => {
    const results: HerxCalendarItem[] = [];
    const all = globalTaskFlowStore.getItems(workspaceId);
    for (const t of all) {
      const proj = projectTaskFlowItemToCalendar(t);
      if (proj) results.push(proj);
    }
    return results;
  }, [workspaceId, refreshTrigger]);

  const handleQuickCreate = () => {
    const created = globalTaskFlowStore.createItem({
      workspaceId,
      type: 'task',
      title: t('taskflow.newTask') || 'New Task',
      status: 'backlog',
    }, 'user');
    setSelectedTask(created);
  };

  const handleExportJson = () => {
    const jsonStr = TaskFlowDataPortability.exportToJson(items, workspaceId);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taskflow-export-${workspaceId}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    const csvStr = TaskFlowDataPortability.exportToCsv(items);
    const blob = new Blob([csvStr], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taskflow-export-${workspaceId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rawContent = event.target?.result as string;
        let result: { success: true; items: TaskFlowItem[] } | { success: false; error: string };

        if (file.name.endsWith('.csv') || (!rawContent.trim().startsWith('{') && !rawContent.trim().startsWith('['))) {
          result = TaskFlowDataPortability.importFromCsv(rawContent);
        } else {
          result = TaskFlowDataPortability.importFromJson(rawContent);
        }

        if (!result.success) {
          const errorMsg = 'error' in result ? (result as { error: string }).error : 'Import failed';
          setImportNotification({ type: 'error', message: errorMsg });
          setTimeout(() => setImportNotification(null), 5000);
          return;
        }

        let importedCount = 0;
        for (const item of result.items) {
          const itemPayload = {
            ...item,
            workspaceId: (item as any).workspaceId || workspaceId,
          };
          const existing = globalTaskFlowStore.getItem(item.id);
          if (existing) {
            globalTaskFlowStore.updateItem(item.id, {
              patch: {
                title: item.title,
                description: item.description,
                status: item.status,
                priority: item.priority,
                startDate: item.startDate,
                dueDate: item.dueDate,
                progressPercentage: item.progressPercentage,
                tags: item.tags,
                parent: item.parent,
              },
              actorId: 'import-user',
            });
          } else {
            globalTaskFlowStore.createItem(itemPayload, 'import-user');
          }
          importedCount++;
        }

        setImportNotification({
          type: 'success',
          message: `✓ Successfully imported ${importedCount} task${importedCount === 1 ? '' : 's'} into the board.`,
        });
        setTimeout(() => setImportNotification(null), 4000);
      } catch (err: any) {
        setImportNotification({
          type: 'error',
          message: `Import failed: ${err.message || 'Unknown error'}`,
        });
        setTimeout(() => setImportNotification(null), 5000);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  // Global Keyboard Shortcuts Dispatcher
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (TaskFlowShortcutManager.shouldIgnoreEvent(e.target as any)) {
        return;
      }

      const cmd = TaskFlowShortcutManager.resolveKey(e.key, e.metaKey || e.ctrlKey, e.shiftKey);

      switch (cmd) {
        case 'SWITCH_VIEW_BOARD':
          e.preventDefault();
          setViewMode('board');
          break;
        case 'SWITCH_VIEW_CALENDAR':
          e.preventDefault();
          setViewMode('calendar');
          break;
        case 'SWITCH_VIEW_TIMELINE':
          e.preventDefault();
          setViewMode('timeline');
          break;
        case 'SWITCH_VIEW_WBS':
          e.preventDefault();
          setViewMode('wbs');
          break;
        case 'SWITCH_VIEW_TRIAGE':
          e.preventDefault();
          setViewMode('triage');
          break;
        case 'CREATE_NEW_TASK':
          e.preventDefault();
          handleQuickCreate();
          break;
        case 'FOCUS_SEARCH_FILTER':
          e.preventDefault();
          searchInputRef.current?.focus();
          break;
        case 'OPEN_HELP_MODAL':
          e.preventDefault();
          setIsShortcutsOpen((prev) => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      role="main"
      aria-label={t('taskflow.title')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'var(--theme-bg-main)',
        color: 'var(--theme-text-primary)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Top Toolbar: View Switcher, Search Bar, Quick Add */}
      <div
        style={{
          padding: isEmbedded ? '8px 12px' : '12px 16px',
          borderBottom: '1px solid var(--theme-border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          backgroundColor: 'var(--theme-bg-header)',
          flexWrap: 'wrap',
        }}
      >
        {/* Left: View Switcher Tabs */}
        <div style={{ display: 'flex', gap: '4px', backgroundColor: 'var(--theme-bg-subtle)', padding: '3px', borderRadius: 'var(--theme-radius-md, 8px)' }}>
          {[
            { id: 'board', label: t('taskflow.views.board') || '📋 Board' },
            { id: 'calendar', label: t('taskflow.views.calendar') || '📅 Calendar' },
            { id: 'timeline', label: t('taskflow.views.timeline') || '📊 Timeline' },
            { id: 'wbs', label: t('taskflow.views.wbs') || '🌲 WBS Tree' },
            { id: 'triage', label: t('taskflow.views.triage') || '⚡ Triage' },
          ].map((tab) => (
            <button
              key={tab.id}
              aria-label={`Switch to ${tab.label}`}
              onClick={() => setViewMode(tab.id as TaskFlowViewMode)}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                border: 'none',
                backgroundColor: viewMode === tab.id ? 'var(--theme-accent)' : 'transparent',
                color: viewMode === tab.id ? 'var(--theme-text-inverse, #fff)' : 'var(--theme-text-secondary)',
                fontSize: 'var(--theme-text-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background-color var(--theme-transition-fast), color var(--theme-transition-fast)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Center: Bounded Query Search Filter */}
        <div style={{ flex: '0 1 340px', position: 'relative' }}>
          <input
            ref={searchInputRef}
            type="text"
            aria-label={t('common.search')}
            placeholder={t('taskflow.searchPlaceholder') || 'Filter: status:active @security (Press F)...'}
            value={queryString}
            onChange={(e) => setQueryString(e.target.value)}
            style={{
              width: '100%',
              padding: '7px 12px 7px 30px',
              backgroundColor: 'var(--theme-bg-input)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-primary)',
              fontSize: 'var(--theme-text-xs)',
              outline: 'none',
            }}
          />
          <span style={{ position: 'absolute', insetInlineStart: '10px', top: '7px', fontSize: 'var(--theme-text-xs)', opacity: 0.5 }}>🔍</span>
          {queryString && (
            <button
              onClick={() => setQueryString('')}
              aria-label={t('common.cancel')}
              style={{
                position: 'absolute',
                insetInlineEnd: '8px',
                top: '7px',
                background: 'none',
                border: 'none',
                color: 'var(--theme-text-muted)',
                cursor: 'pointer',
                fontSize: 'var(--theme-text-xs)',
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Right: Swimlane Switcher, Rules, Shortcuts, Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {viewMode === 'board' && (
            <select
              aria-label="Group items by swimlane axis"
              value={swimlaneAxis}
              onChange={(e) => setSwimlaneAxis(e.target.value as SwimlaneGroupingAxis)}
              style={{
                backgroundColor: 'var(--theme-bg-input)',
                color: 'var(--theme-text-secondary)',
                border: '1px solid var(--theme-border)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                padding: '6px 8px',
                fontSize: 'var(--theme-text-xs)',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="none">{t('taskflow.grouping.none') || 'Group: None'}</option>
              <option value="type">{t('taskflow.grouping.type') || 'Group: Type'}</option>
              <option value="priority">{t('taskflow.grouping.priority') || 'Group: Priority'}</option>
              <option value="tag">{t('taskflow.grouping.tag') || 'Group: Tag'}</option>
            </select>
          )}

          {/* Template Picker Toggle */}
          <button
            onClick={() => setIsTemplatePickerOpen(true)}
            title={t('templates.title')}
            aria-label={t('taskflow.templates')}
            style={{
              padding: '6px 10px',
              backgroundColor: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>📂</span>
            <span>{t('taskflow.templates') || 'Templates'}</span>
          </button>

          {/* Hidden File Input for Task Board Import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.csv"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />

          {/* Import Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            title={t('taskflow.import') || 'Import tasks from JSON or CSV file'}
            aria-label="Import tasks from JSON or CSV"
            style={{
              padding: '6px 10px',
              backgroundColor: 'var(--theme-bg-subtle, rgba(56, 189, 248, 0.15))',
              border: '1px solid var(--theme-border-accent, rgba(56, 189, 248, 0.35))',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-accent, #38bdf8)',
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s ease',

            }}
          >
            <span>📥</span>
            <span>{t('taskflow.import') || 'Import'}</span>
          </button>

          {/* Export Buttons */}
          <button
            onClick={handleExportJson}
            title={t('taskflow.exportJson')}
            aria-label="Export items to JSON"
            style={{
              padding: '6px 8px',
              backgroundColor: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-xs)',
              cursor: 'pointer',
            }}
          >
            {t('taskflow.exportJson') || 'JSON'}
          </button>

          <button
            onClick={handleExportCsv}
            title={t('taskflow.exportCsv')}
            aria-label="Export items to CSV"
            style={{
              padding: '6px 8px',
              backgroundColor: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-xs)',
              cursor: 'pointer',
            }}
          >
            {t('taskflow.exportCsv') || 'CSV'}
          </button>

          {/* Automation Rules Drawer Toggle */}
          <button
            onClick={() => setIsAutomationOpen(true)}
            title={t('automation.title')}
            aria-label={t('taskflow.rules')}
            style={{
              padding: '6px 10px',
              backgroundColor: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>⚡</span>
            <span>{t('taskflow.rules') || 'Rules'}</span>
          </button>

          {/* Keyboard Shortcuts Cheatsheet Toggle */}
          <button
            onClick={() => setIsShortcutsOpen(true)}
            title={t('shortcuts.title')}
            aria-label={t('taskflow.shortcuts')}
            style={{
              padding: '6px 8px',
              backgroundColor: 'var(--theme-bg-subtle)',
              border: '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              color: 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-xs)',
              cursor: 'pointer',
            }}
          >
            ⌨️
          </button>

          <span style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)' }}>
            <bdi>{items.length} {t('common.actions') ? '' : ''}</bdi>
          </span>

          <button
            onClick={handleQuickCreate}
            aria-label="Create new task (N)"
            style={{
              padding: '6px 14px',
              backgroundColor: 'var(--theme-accent)',
              color: 'var(--theme-text-inverse, #ffffff)',
              border: 'none',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: 'var(--theme-shadow-sm)',
            }}
          >
            <span>+</span>
            <span>{t('taskflow.newTask') || 'New Task'} (N)</span>
          </button>
        </div>
      </div>

      {/* Import Notification Banner */}
      {importNotification && (
        <div
          style={{
            padding: '8px 16px',
            backgroundColor: importNotification.type === 'success' ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            borderBottom: `1px solid ${importNotification.type === 'success' ? '#34d399' : '#ef4444'}`,
            color: importNotification.type === 'success' ? '#6ee7b7' : '#fca5a5',
            fontSize: '12px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            animation: 'fadeIn 0.2s ease-in-out',
          }}
        >
          <span>{importNotification.message}</span>
          <button
            type="button"
            onClick={() => setImportNotification(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Main Viewport Content */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {viewMode === 'board' && (
          <SwimlaneContainer
            items={items}
            groupingAxis={swimlaneAxis}
            renderGroupContent={(groupItems) => (
              <TaskFlowBoardView
                workspaceId={workspaceId}
                items={groupItems}
                selectedTaskId={selectedTask?.id}
                onSelectTask={(task) => setSelectedTask(task)}
              />
            )}
          />
        )}

        {viewMode === 'calendar' && (
          <UnifiedCalendarView
            items={calendarItems}
            workspaceId={workspaceId}
            initialViewMode="month"
            onSelectItem={(calItem) => {
              if (calItem.source === 'taskflow') {
                const found = globalTaskFlowStore.getItem(calItem.sourceId);
                if (found) setSelectedTask(found);
              }
            }}
          />
        )}

        {viewMode === 'timeline' && (
          <TaskFlowTimelineView
            workspaceId={workspaceId}
            items={items}
            selectedTaskId={selectedTask?.id}
            onSelectTask={(task) => setSelectedTask(task)}
          />
        )}

        {viewMode === 'wbs' && (
          <TaskFlowWbsView
            workspaceId={workspaceId}
            items={items}
            selectedTaskId={selectedTask?.id}
            onSelectTask={(task) => setSelectedTask(task)}
          />
        )}

        {viewMode === 'triage' && (
          <TaskFlowTriageView
            workspaceId={workspaceId}
            items={items}
            onSelectTask={(task) => setSelectedTask(task)}
          />
        )}
      </div>

      {/* Slide-out Task Detail Drawer */}
      <TaskDetailDrawer
        itemId={selectedTask ? selectedTask.id : null}
        workspaceId={workspaceId}
        onClose={() => setSelectedTask(null)}
        onItemUpdated={(updated) => setSelectedTask(updated)}
      />

      {/* Slide-out Automation Rule Builder */}
      {isAutomationOpen && (
        <AutomationRuleEditor
          items={items}
          onClose={() => setIsAutomationOpen(false)}
        />
      )}

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={isTemplatePickerOpen}
        workspaceId={workspaceId}
        onClose={() => setIsTemplatePickerOpen(false)}
        onInstantiated={() => setRefreshTrigger((prev) => prev + 1)}
      />

      {/* Keyboard Shortcuts Cheatsheet Modal */}
      <KeyboardShortcutsModal
        isOpen={isShortcutsOpen}
        onClose={() => setIsShortcutsOpen(false)}
      />

      {/* Floating Multi-Select Bulk Action Toolbar */}
      <BulkActionBar
        selectedIds={selectedIds}
        workspaceId={workspaceId}
        onClearSelection={() => setSelectedIds([])}
        onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
      />
    </div>
  );
};
